import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FileUpload } from '../components/FileUpload';
import { DetectReceiptFraudRequestForm, DetectReceiptFraudRequestFormRef, FormDataWithAuth } from '../components/DetectReceiptFraudRequestForm';
import { DetectReceiptFraudResults } from '../components/DetectReceiptFraudResults';
import { detectReceiptFraud, ping, ApiError } from '../api';
import { DetectReceiptFraudRequest, DetectReceiptFraudResponse } from '../types';
import { config } from '../config';

enum ProcessingStep {
  FILE_SELECTION = 'file_selection',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  ERROR = 'error'
}

interface DocumentState {
  id: string;
  file: File;
  previewUrl: string;
  step: ProcessingStep;
  results: DetectReceiptFraudResponse | null;
  error: string;
}

const CACHE_KEY = 'formx-detect-receipt-fraud-form-data';

export function DetectReceiptFraud() {
  // Load cached form data
  const loadCachedData = (): FormDataWithAuth => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsedData = JSON.parse(cached);
        return {
          ...config.defaultDetectReceiptFraudFormValues,
          ...parsedData,
          auth_token: parsedData.auth_token || ''
        };
      }
    } catch (error) {
      console.warn('Failed to load cached form data:', error);
    }
    
    return {
      auth_token: '',
      llm_model_provider: config.defaultDetectReceiptFraudFormValues.llmModelProvider,
      llm_model_name: config.defaultDetectReceiptFraudFormValues.llmModelName,
      chat_complete_request_args: null,
      chat_complete_request_args_enabled: false,
      openrouter_api_key: null,
      extracted_data: config.defaultDetectReceiptFraudFormValues.extractedData,
      custom_instructions: null,
      custom_instructions_enabled: true,
      max_allowed_tool_calls: null,
    };
  };

  const [documents, setDocuments] = useState<DocumentState[]>([]);
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isCheckingConnection, setIsCheckingConnection] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormDataWithAuth>(loadCachedData);
  const [isConfigCollapsed, setIsConfigCollapsed] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(true); // Default to true as form is initially valid
  const formRef = useRef<DetectReceiptFraudRequestFormRef>(null);
  const documentsRef = useRef<DocumentState[]>([]);

  // Keep ref in sync with documents state
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  // Get active document
  const activeDocument = documents.find(doc => doc.id === activeDocumentId) || null;

  // Validation utility function for fallback when form is not mounted
  const validateFormData = useCallback((data: FormDataWithAuth): boolean => {
    // Basic validation - ensure extracted_data is present
    return true; // Form validation is handled by the form component itself
  }, []);

  // Initialize form validity based on cached data
  useEffect(() => {
    setIsFormValid(validateFormData(loadCachedData()));
  }, [validateFormData]);

  // Cache form data whenever it changes
  const handleFormDataChange = (newFormData: FormDataWithAuth) => {
    setFormData(newFormData);
    // Check form validity using ref if form is mounted, otherwise use callback validation
    const isValid = formRef.current ? formRef.current.isValid() : validateFormData(newFormData);
    setIsFormValid(isValid);
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(newFormData));
    } catch (error) {
      console.warn('Failed to cache form data:', error);
    }
  };

  const handleValidationChange = (isValid: boolean) => {
    setIsFormValid(isValid);
  };

  const handleCheckConnection = async () => {
    setIsCheckingConnection(true);
    try {
      await ping();
      setIsConnected(true);
      toast.success('Successfully connected to backend');
    } catch (err) {
      setIsConnected(false);
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to connect to backend';
      toast.error(errorMessage);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  const handleFileSelect = async (file: File) => {
    // Create new document
    const documentId = `doc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const previewUrl = URL.createObjectURL(file);
    
    const newDocument: DocumentState = {
      id: documentId,
      file,
      previewUrl,
      step: ProcessingStep.FILE_SELECTION,
      results: null,
      error: '',
    };
    
    setDocuments(prev => [...prev, newDocument]);
    setActiveDocumentId(documentId);
    toast.success(`File "${file.name}" added successfully`);
  };
  
  const handleOpenImage = () => {
    if (activeDocument?.previewUrl) {
      window.open(activeDocument.previewUrl, '_blank');
    }
  };

  const handleFormSubmit = async (formData: DetectReceiptFraudRequest, formAuthToken: string) => {
    if (!activeDocument) {
      const errorMessage = 'No document selected';
      toast.error(errorMessage);
      return;
    }

    // Update document state to processing
    setDocuments(prev => prev.map(doc => 
      doc.id === activeDocumentId 
        ? { ...doc, step: ProcessingStep.PROCESSING, error: '', results: null }
        : doc
    ));

    try {
      const response = await detectReceiptFraud(activeDocument.file, formData, formAuthToken);
      
      // Update document state to completed with results
      setDocuments(prev => prev.map(doc => 
        doc.id === activeDocumentId 
          ? { ...doc, step: ProcessingStep.COMPLETED, results: response }
          : doc
      ));
      
      toast.success('Receipt fraud detection completed');
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? `${err.message}` 
        : 'Processing failed';
      
      // Update document state to error
      setDocuments(prev => prev.map(doc => 
        doc.id === activeDocumentId 
          ? { ...doc, step: ProcessingStep.ERROR, error: errorMessage }
          : doc
      ));
      
      toast.error(errorMessage);
    }
  };

  const handleRemoveDocument = (documentId: string) => {
    const docToRemove = documents.find(doc => doc.id === documentId);
    if (docToRemove) {
      URL.revokeObjectURL(docToRemove.previewUrl);
    }
    
    setDocuments(prev => prev.filter(doc => doc.id !== documentId));
    
    // If removing active document, select another one
    if (activeDocumentId === documentId) {
      const remainingDocs = documents.filter(doc => doc.id !== documentId);
      setActiveDocumentId(remainingDocs.length > 0 ? remainingDocs[0].id : null);
    }
    
    toast.info('Document removed');
  };

  const handleSwitchDocument = (documentId: string) => {
    setActiveDocumentId(documentId);
  };

  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };

  const isProcessing = activeDocument?.step === ProcessingStep.PROCESSING;

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      // Use ref to access latest documents at cleanup time
      documentsRef.current.forEach(doc => {
        URL.revokeObjectURL(doc.previewUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run cleanup on unmount

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <h1>FormX Receipt Fraud Detection</h1>
          <nav className="app-nav" aria-label="Main">
            <button 
              className="btn btn-nav active"
              onClick={() => window.open('/detect-receipt-fraud', '_blank')}
              aria-current="page"
            >
              Receipt Fraud Detection
            </button>
            <button 
              className="btn btn-nav"
              onClick={() => window.open('/detect-abnormal-layout', '_blank')}
            >
              Abnormal Layout Detection
            </button>
          </nav>
        </div>
        <div className="connection-status">
          {isConnected === null && (
            <button 
              onClick={handleCheckConnection} 
              className="btn btn-secondary"
              disabled={isCheckingConnection}
            >
              {isCheckingConnection ? 'Checking...' : 'Check Connection'}
            </button>
          )}
          {isConnected === true && (
            <>
              <span className="status-connected">✓ Connected</span>
              <button 
                onClick={handleCheckConnection} 
                className="btn btn-secondary"
                disabled={isCheckingConnection}
              >
                {isCheckingConnection ? 'Checking...' : 'Recheck'}
              </button>
            </>
          )}
          {isConnected === false && (
            <>
              <span className="status-disconnected">✗ Backend unavailable</span>
              <button 
                onClick={handleCheckConnection} 
                className="btn btn-secondary"
                disabled={isCheckingConnection}
              >
                {isCheckingConnection ? 'Checking...' : 'Retry Connection'}
              </button>
            </>
          )}
        </div>
      </header>

      <main className="app-main">
        <div className="app-layout">
          <div className="left-panel">
            <div className="upload-section">
              <h2>Upload Receipt Images</h2>
              <FileUpload onFileSelect={handleFileSelect} disabled={isProcessing} multiple={true} />
              
              {/* Document List */}
              {documents.length > 0 && (
                <div className="document-list">
                  <h3>Uploaded Documents ({documents.length})</h3>
                  <div className="document-items">
                    {documents.map(doc => (
                      <div 
                        key={doc.id}
                        className={`document-item ${doc.id === activeDocumentId ? 'active' : ''} ${doc.step === ProcessingStep.PROCESSING ? 'processing' : ''}`}
                        onClick={() => handleSwitchDocument(doc.id)}
                      >
                        <div className="document-item-content">
                          <div className="document-icon">
                            {doc.step === ProcessingStep.PROCESSING && '⏳'}
                            {doc.step === ProcessingStep.COMPLETED && '✓'}
                            {doc.step === ProcessingStep.ERROR && '✗'}
                            {doc.step === ProcessingStep.FILE_SELECTION && '📄'}
                          </div>
                          <div className="document-info">
                            <div className="document-name" title={doc.file.name}>
                              {doc.file.name}
                            </div>
                            <div className="document-meta">
                              {(doc.file.size / 1024 / 1024).toFixed(2)} MB
                              {doc.step === ProcessingStep.PROCESSING && ' • Processing...'}
                              {doc.step === ProcessingStep.COMPLETED && ' • Completed'}
                              {doc.step === ProcessingStep.ERROR && ' • Error'}
                            </div>
                          </div>
                        </div>
                        <button
                          className="btn btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveDocument(doc.id);
                          }}
                          title="Remove document"
                          disabled={doc.step === ProcessingStep.PROCESSING}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Active Document Actions */}
              {activeDocument && (
                <div className="active-document-actions">
                  <button 
                    onClick={handleOpenImage} 
                    className="btn btn-secondary"
                    title="Open image in new tab"
                  >
                    🔍 Open Image
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    disabled={!isFormValid || isProcessing}
                    onClick={() => {
                      const { auth_token, chat_complete_request_args_enabled, custom_instructions_enabled, ...requestData } = formData;
                      const payload = {
                        ...requestData,
                        chat_complete_request_args: chat_complete_request_args_enabled 
                          ? formData.chat_complete_request_args 
                          : null,
                        custom_instructions: custom_instructions_enabled 
                          ? formData.custom_instructions 
                          : null,
                      };
                      handleFormSubmit(payload, auth_token);
                    }}
                  >
                    {isProcessing ? 'Processing...' : 'Detect Fraud'}
                  </button>
                </div>
              )}
            </div>

            <div className="config-section">
              <div 
                className="config-header" 
                onClick={() => setIsConfigCollapsed(!isConfigCollapsed)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsConfigCollapsed(!isConfigCollapsed);
                  }
                }}
                aria-expanded={!isConfigCollapsed}
                aria-controls="config-form"
              >
                <button 
                  type="button" 
                  className={`collapse-toggle ${isConfigCollapsed ? 'collapsed' : ''}`}
                  aria-label={isConfigCollapsed ? 'Expand configuration' : 'Collapse configuration'}
                >
                  <span>▼</span>
                </button>
                <h2>Configure Detection</h2>
              </div>
              {!isConfigCollapsed && (
                <div id="config-form">
                  <DetectReceiptFraudRequestForm 
                    ref={formRef}
                    formData={formData}
                    onFormDataChange={handleFormDataChange}
                    onSubmit={(requestData, authToken) => handleFormSubmit(requestData, authToken)}
                    disabled={isProcessing}
                    onValidationChange={handleValidationChange}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Middle Panel - Image Preview */}
          <div className="middle-panel">
            {activeDocument && (
              <div className="image-preview-panel">
                <div className="image-preview-header">
                  <h3>Receipt Image Preview</h3>
                  <button 
                    onClick={handleOpenImage} 
                    className="btn btn-secondary btn-sm"
                    title="Open image in new tab"
                  >
                    🔍 Open in New Tab
                  </button>
                </div>
                <div className="image-preview-content">
                  <img 
                    src={activeDocument.previewUrl} 
                    alt={activeDocument.file.name} 
                    className="preview-image-full"
                    onClick={handleOpenImage}
                  />
                </div>
                <div className="image-preview-footer">
                  <span className="image-filename">{activeDocument.file.name}</span>
                  <span className="image-filesize">{(activeDocument.file.size / 1024 / 1024).toFixed(2)} MB</span>
                </div>
              </div>
            )}

            {!activeDocument && documents.length === 0 && (
              <div className="placeholder-message">
                <h3>No receipt selected</h3>
                <p>Upload a receipt to see the preview here</p>
              </div>
            )}

            {!activeDocument && documents.length > 0 && (
              <div className="placeholder-message">
                <h3>No receipt selected</h3>
                <p>Select a document from the list to preview it here</p>
              </div>
            )}
          </div>

          {/* Right Panel - Results */}
          <div className="right-panel">
            {activeDocument?.step === ProcessingStep.PROCESSING && (
              <div className="status-message">
                <h3>Analyzing receipt...</h3>
                <p>This may take a few moments.</p>
                <div className="loading-spinner"></div>
              </div>
            )}

            {activeDocument?.step === ProcessingStep.ERROR && (
              <div className="error-message">
                <h3>Error</h3>
                <p>{activeDocument.error}</p>
              </div>
            )}

            {activeDocument?.step === ProcessingStep.COMPLETED && activeDocument.results && (
              <div className="results-container">
                <DetectReceiptFraudResults 
                  results={activeDocument.results} 
                  onDownload={handleDownload}
                />
              </div>
            )}

            {activeDocument && !activeDocument.results && activeDocument.step === ProcessingStep.FILE_SELECTION && (
              <div className="placeholder-message">
                <h3>Ready to detect fraud</h3>
                <p>Configure your settings and click "Detect Fraud" to begin.</p>
              </div>
            )}

            {!activeDocument && documents.length === 0 && (
              <div className="placeholder-message">
                <h3>Upload receipt images to get started</h3>
                <p>You can upload multiple receipts and process them individually.</p>
              </div>
            )}

            {!activeDocument && documents.length > 0 && (
              <div className="placeholder-message">
                <h3>Select a document</h3>
                <p>Click on a document from the list to view its details and results.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
